import { Check, Minus } from '@/shared/icons/phosphor'
import { type JSX, Show, createMemo, splitProps } from 'solid-js'
import { RequiredBadge } from '../Badge/RequiredBadge'

export type CheckboxSize = 'sm' | 'md'

export interface CheckboxProps
  extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'type' | 'size'> {
  label?: JSX.Element
  description?: string
  error?: string
  size?: CheckboxSize
  indeterminate?: boolean
  requiredMark?: boolean
}

const boxSize: Record<CheckboxSize, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4 w-4',
}

const markSize: Record<CheckboxSize, string> = {
  sm: 'h-2.5 w-2.5',
  md: 'h-3 w-3',
}

export function Checkbox(props: CheckboxProps) {
  const [local, rest] = splitProps(props, [
    'label',
    'description',
    'error',
    'size',
    'class',
    'id',
    'readOnly',
    'disabled',
    'requiredMark',
  ])
  const size = () => local.size ?? 'md'
  const inputId = createMemo(() => local.id ?? `checkbox-${Math.random().toString(36).slice(2, 9)}`)
  const invalid = () => (local.error ?? '') !== ''
  const readOnly = () => local.readOnly === true

  return (
    <div class={`min-w-0 flex flex-col gap-1 ${local.class ?? ''}`}>
      <label
        for={inputId()}
        class={`flex min-w-0 items-start gap-2.5 ${
          readOnly()
            ? 'cursor-default'
            : 'cursor-pointer has-disabled:cursor-not-allowed has-disabled:opacity-50'
        }`}
      >
        <span class={`relative mt-0.5 inline-flex shrink-0 ${boxSize[size()]}`}>
          <input
            {...rest}
            id={inputId()}
            type="checkbox"
            disabled={local.disabled || readOnly()}
            aria-invalid={invalid() || undefined}
            aria-required={local.requiredMark ? true : rest['aria-required']}
            class={`
              peer ${boxSize[size()]} m-0 shrink-0 appearance-none
              rounded-[var(--radius-check)] border border-[var(--color-check-border)]
              bg-[var(--color-surface)]
              checked:border-[var(--color-primary-500)] checked:bg-[var(--color-primary-500)]
              indeterminate:border-[var(--color-primary-500)] indeterminate:bg-[var(--color-primary-500)]
              transition-colors duration-[var(--duration-fast)] ease-[var(--ease-component)]
              focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary-500)]
              ${readOnly() ? 'cursor-default' : 'cursor-pointer disabled:cursor-not-allowed'}
              ${invalid() ? 'shadow-[var(--focus-ring-error)]' : ''}
            `.trim()}
          />
          <Check
            weight="bold"
            aria-hidden="true"
            class={`pointer-events-none absolute inset-0 m-auto ${markSize[size()]} text-[color:var(--color-on-primary)] transition-[opacity,transform] duration-[var(--duration-normal)] scale-75 peer-checked:scale-100 peer-indeterminate:scale-100 opacity-0 peer-checked:opacity-100 peer-indeterminate:opacity-0`}
          />
          <Minus
            aria-hidden="true"
            class={`pointer-events-none absolute inset-0 m-auto ${markSize[size()]} text-[color:var(--color-on-primary)] transition-[opacity,transform] duration-[var(--duration-normal)] scale-75 peer-checked:scale-100 peer-indeterminate:scale-100 opacity-0 peer-indeterminate:opacity-100`}
          />
        </span>
        <Show when={local.label}>
          <span class="min-w-0 flex-1 flex flex-col gap-0.5">
            <span class="block text-pretty text-[length:var(--text-sm)] text-[color:var(--color-text)] leading-[var(--leading-snug)]">
              {local.label}
              <Show when={local.requiredMark}>
                <RequiredBadge announced={false} />
              </Show>
            </span>
            <Show when={local.description}>
              <span class="text-pretty text-[length:var(--text-xs)] text-[color:var(--color-text-muted)]">
                {local.description}
              </span>
            </Show>
          </span>
        </Show>
      </label>
      <Show when={local.error}>
        <p class="text-[length:var(--text-xs)] text-[color:var(--color-error)]">{local.error}</p>
      </Show>
    </div>
  )
}
